import { useEffect, useState } from 'react';
import Image from 'next/image';
import FFHModal from '@components/modal';
import Button from '@components/atoms/button';
import Icon from '@components/atoms/icon';
import { BodyRegular } from '@components/atoms/typography';
import {
  PlayerCircle,
  ClubBadgeCircle,
  CONTROL_SIZE
} from '@components/manual-team-entry/player-circle';
import {
  FREE_PREDICTIONS_GAMEWEEK,
  RECOMMEND_TRANSFER_PLACEMENT
} from '@components/pick/constants';
import { LockedAction } from '@components/pick/LockedAction';
import { getPredictedPointsLevelStyles } from '@components/fixtures/utils/fixtureTypes';
import { PlayerNewsNote } from '@components/player/PlayerNewsNote';
import {
  type AvailabilitySeverity,
  getAvailabilityLabel,
  getAvailabilitySeverity,
  type PlayerStatus
} from '@lib/my-team/player-availability';
import { useAccessToken } from '@hooks/useAccessToken';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { usePlayers } from '@hooks/usePlayers';
import { usePlayerStats } from '@hooks/usePlayerStats';
import type { Player, PlayerPosition } from '@api-service/players';
import type { PlayerStatsRow } from '@api-service/player-stats';

/** Prediction seed shared by planner squad players and player-list rows. */
// Availability pill per severity: amber for a mild doubt, red for anything
// worse, neutral when the label is informational only.
const AVAILABILITY_PILL_STYLES: Record<AvailabilitySeverity, string> = {
  none: 'bg-neutral-200 text-neutral-700',
  caution: 'bg-warning-100 text-warning-700',
  danger: 'bg-danger-50 text-danger-600'
};

export type PlayerInfoSeedFixture = {
  gameweek: number;
  opponent: { shortName: string };
  isHome: boolean;
  predictedPoints: number | null;
};

/**
 * The player the modal describes, normalised so it can be opened from the
 * pitch (a planner squad player) or from any player row (a list player)
 * without the modal knowing about either shape.
 */
export type PlayerInfoSubject = {
  id: string;
  displayName: string;
  position: PlayerPosition;
  teamShortName: string;
  price: number;
  status?: PlayerStatus;
  chanceOfPlaying?: number | null;
  /** Paints the prediction grid before the players response lands. */
  seedFixtures: PlayerInfoSeedFixture[];
};

/** Squad actions, omitted for a player who is not in the user's squad. */
export type PlayerInfoActions = {
  isCaptain: boolean;
  isViceCaptain: boolean;
  isPending: boolean;
  isTransferModeActive: boolean;
  onMakeCaptain: () => void;
  onMakeViceCaptain: () => void;
  onStartSubstitution: () => void;
  onTransferOut: () => void;
  /** False when the player is not in the squad the transfers builder uses. */
  canRecommendTransfer: boolean;
  onRecommendTransfer: () => void;
};

type PlayerInfoModalProps = {
  subject: PlayerInfoSubject | null;
  onClose: () => void;
  /**
   * Gameweek the six-gameweek window starts at: the one the caller is
   * showing, so the modal's figures match the fixtures on screen behind it.
   * Callers always know it, so the grid paints on open with nothing to wait
   * for and the dialog does not resize under the reader's finger.
   */
  startGameweek: number;
  actions?: PlayerInfoActions | null;
  /**
   * Non-paying members keep the free gameweek's prediction column; every
   * other column shows a padlock (mirroring the pitch tiles) and the total
   * strip is swapped for a membership CTA.
   */
  isPayingMember?: boolean;
};

/** Tailwind's md breakpoint: below it the modal renders as a bottom sheet. */
const MOBILE_VIEWPORT_QUERY = '(max-width: 767px)';

const PREDICTED_GAMEWEEK_COUNT = 6;

/** Suppress the over/under-expected chip when the delta rounds to 0.00. */
const DELTA_EPSILON = 0.005;

/** Step back one season when the requested season has no Opta output. */
const PREVIOUS_SEASON_OFFSET = 1;

/** Fixtures with likelihood below 1 are predicted, not confirmed. */
const CONFIRMED_FIXTURE_LIKELIHOOD = 1;

const MOBILE_PORTRAIT_SIZE = 64;
const DESKTOP_PORTRAIT_SIZE = 72;

/** Predictions rarely move between refreshes, so cache them generously. */
const PLAYER_DETAIL_STALE_TIME_MS = 15 * 60 * 1000;

const POSITION_FULL_NAMES: Record<PlayerPosition, string> = {
  GK: 'Goalkeeper',
  DEF: 'Defender',
  MID: 'Midfielder',
  FWD: 'Forward'
};

const SEASON_LABEL_YEAR_LENGTH = 2;

/** 2025 renders as "2025/26". */
const formatSeasonLabel = (season: number) =>
  `${season}/${((season + 1) % 100)
    .toString()
    .padStart(SEASON_LABEL_YEAR_LENGTH, '0')}`;

// matchMedia is absent during SSR and in jsdom test runs, so both the
// initialiser and the effect check for it before use.
const canQueryViewport = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

const useIsMobileViewport = () => {
  // Initialised from matchMedia so the sheet does not flash centred before
  // the effect runs on a phone.
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => canQueryViewport() && window.matchMedia(MOBILE_VIEWPORT_QUERY).matches
  );

  useEffect(() => {
    if (!canQueryViewport()) return undefined;

    const mediaQueryList = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    const updateViewport = () => setIsMobileViewport(mediaQueryList.matches);
    updateViewport();
    mediaQueryList.addEventListener('change', updateViewport);
    return () => mediaQueryList.removeEventListener('change', updateViewport);
  }, []);

  return isMobileViewport;
};

type UsePlayerSeasonStatsOptions = {
  /** Current-season player id, valid as a stats filter only for that season. */
  playerId: string | null;
  /** Stable cross-season Opta id, used to resolve past-season player ids. */
  optaUuid: string | null;
  /** Field groups the position's layout reads, e.g. 'fpl,defending'. */
  fields: string;
  season: number | null;
  currentSeason: number | null;
  enabled: boolean;
  accessToken: string | null | undefined;
};

type PlayerSeasonStats = {
  row: PlayerStatsRow | null;
  isLoading: boolean;
  /** The season resolved but holds no Opta output for this player. */
  isEmpty: boolean;
};

/**
 * Per-start Opta stats for one player in one season.
 *
 * Player ids are re-minted every season and the stats endpoint only filters
 * by the id belonging to the season being asked for, so anything but the
 * current season needs the player's cross-season Opta id resolved to that
 * season's row first.
 */
const usePlayerSeasonStats = ({
  playerId,
  optaUuid,
  fields,
  season,
  currentSeason,
  enabled,
  accessToken
}: UsePlayerSeasonStatsOptions): PlayerSeasonStats => {
  const needsResolution = season != null && season !== currentSeason;

  const { data: rawResolvedData, isPreviousData: isResolvedDataPrevious } =
    usePlayers({
      optaIds: optaUuid ?? undefined,
      season: season ?? undefined,
      // Only the id is wanted here, so skip the prediction enrichment.
      exclude: 'predictions',
      limit: 1,
      enabled: enabled && needsResolution && optaUuid != null,
      staleTime: PLAYER_DETAIL_STALE_TIME_MS,
      accessToken: accessToken ?? undefined
    });
  // keepPreviousData keeps serving the previous player's rows while the new
  // ones load. Those must read as "still loading" here, or the modal opens on
  // the last player's figures and swaps them mid-read.
  const resolvedData = isResolvedDataPrevious ? undefined : rawResolvedData;
  const resolvedPlayerId = resolvedData?.pages[0]?.data[0]?.id ?? null;
  const isResolutionPending =
    needsResolution && (optaUuid == null || resolvedData == null);
  // A player who was not in the league that season has no row to read.
  const hasNoSeasonPlayer =
    needsResolution && resolvedData != null && resolvedPlayerId == null;

  const seasonPlayerId = needsResolution ? resolvedPlayerId : playerId;

  const { data: rawStatsData, isPreviousData: isStatsDataPrevious } =
    usePlayerStats({
      ids: seasonPlayerId ?? undefined,
      fields,
      aggregation: 'perStart',
      season,
      limit: 1,
      enabled: enabled && season != null && seasonPlayerId != null,
      staleTime: PLAYER_DETAIL_STALE_TIME_MS,
      accessToken: accessToken ?? undefined
    });
  const statsData = isStatsDataPrevious ? undefined : rawStatsData;
  const row = statsData?.pages[0]?.data[0] ?? null;

  if (!enabled || season == null) {
    return { row: null, isLoading: false, isEmpty: false };
  }

  if (hasNoSeasonPlayer) {
    return { row: null, isLoading: false, isEmpty: true };
  }

  return {
    row,
    isLoading: row == null && (isResolutionPending || statsData == null),
    isEmpty: row == null && !isResolutionPending && statsData != null
  };
};

/** Read a dotted path off a loosely typed stats row, defaulting to zero. */
const readStatNumber = (row: PlayerStatsRow, path: string): number => {
  const segments = path.split('.');
  let cursor: unknown = row;
  for (const segment of segments) {
    if (cursor && typeof cursor === 'object' && segment in cursor) {
      cursor = (cursor as Record<string, unknown>)[segment];
    } else {
      return 0;
    }
  }
  return typeof cursor === 'number' ? cursor : 0;
};

type StatCardConfig = {
  label: string;
  realisedPath: string;
  expectedPath: string;
  expectedLabel: string;
  /** Under expected is the good outcome, e.g. goals conceded. */
  isUnderExpectedGood?: boolean;
};

type SecondaryTileConfig = {
  label: string;
  path: string;
};

type SeasonStatsLayout = {
  /** Field groups the stats request needs for this position's figures. */
  fields: string;
  cards: StatCardConfig[];
  /** Two tiles; the starts count always takes the third slot. */
  tiles: SecondaryTileConfig[];
};

const FORWARD_STATS_LAYOUT: SeasonStatsLayout = {
  fields: 'fpl,shooting,creativity',
  cards: [
    {
      label: 'Goals',
      realisedPath: 'shooting.goals.total',
      expectedPath: 'shooting.expectedGoals',
      expectedLabel: 'xG'
    },
    {
      label: 'Assists',
      realisedPath: 'creativity.assists',
      expectedPath: 'creativity.expectedAssists',
      expectedLabel: 'xA'
    },
    {
      label: 'Points',
      realisedPath: 'fpl.points',
      expectedPath: 'fpl.expectedPoints',
      expectedLabel: 'xPts'
    }
  ],
  tiles: [
    { label: 'Shots in box', path: 'shooting.shots.inBox' },
    { label: 'Key passes', path: 'creativity.keyPasses' }
  ]
};

// A defender earns 2 points for 10+ CBIT (clearances, blocks, interceptions,
// tackles) in a match, so per-start CBIT reads directly against that
// threshold and takes the shots-in-box slot.
const DEFENDER_STATS_LAYOUT: SeasonStatsLayout = {
  ...FORWARD_STATS_LAYOUT,
  fields: 'fpl,shooting,creativity,defensiveActions',
  tiles: [
    { label: 'Defcon', path: 'defensiveActions.cbit' },
    { label: 'Key passes', path: 'creativity.keyPasses' }
  ]
};

// Midfield DefCon counts recoveries too (CBIRT, 12+ for the 2 points), so
// the midfielder tile reads a different action count than the defender one.
const MIDFIELDER_STATS_LAYOUT: SeasonStatsLayout = {
  ...DEFENDER_STATS_LAYOUT,
  tiles: [
    { label: 'Defcon', path: 'defensiveActions.cbitr' },
    { label: 'Key passes', path: 'creativity.keyPasses' }
  ]
};

// Goalkeepers rarely score or assist and earn no defensive-contribution
// points, so their headline pairings are clean sheets against expected and
// goals conceded against xGC, where under expected is the good outcome.
const GOALKEEPER_STATS_LAYOUT: SeasonStatsLayout = {
  fields: 'fpl,defending',
  cards: [
    {
      label: 'Clean sheets',
      realisedPath: 'defending.cleanSheets',
      expectedPath: 'defending.expectedCleanSheets',
      expectedLabel: 'xCS'
    },
    {
      label: 'Goals conceded',
      realisedPath: 'defending.goalsConceded',
      expectedPath: 'defending.expectedGoalsConceded',
      expectedLabel: 'xGC',
      isUnderExpectedGood: true
    },
    {
      label: 'Points',
      realisedPath: 'fpl.points',
      expectedPath: 'fpl.expectedPoints',
      expectedLabel: 'xPts'
    }
  ],
  tiles: [
    { label: 'Saves', path: 'fpl.saves' },
    { label: 'Shots on target faced', path: 'defending.shotsConceded.onTarget' }
  ]
};

const SEASON_STATS_LAYOUTS: Record<PlayerPosition, SeasonStatsLayout> = {
  GK: GOALKEEPER_STATS_LAYOUT,
  DEF: DEFENDER_STATS_LAYOUT,
  MID: MIDFIELDER_STATS_LAYOUT,
  FWD: FORWARD_STATS_LAYOUT
};

type PredictionFixture = {
  opponentShortName: string;
  isHome: boolean;
};

type PredictionColumn = {
  gameweek: number;
  /** Null while only planner data (points, no minutes) is available. */
  points: number | null;
  minutes: number | null;
  fixtures: PredictionFixture[];
  isProvisional: boolean;
};

/**
 * One column per gameweek in the window. Double gameweeks sum points and
 * minutes and list both opponents; blank gameweeks keep an empty column so
 * the grid always shows six.
 */
const buildPredictionColumns = (
  firstGameweek: number,
  detailedPlayer: Player | null,
  seedFixtures: PlayerInfoSeedFixture[]
): PredictionColumn[] => {
  const columns = new Map<number, PredictionColumn>();
  for (let offset = 0; offset < PREDICTED_GAMEWEEK_COUNT; offset += 1) {
    const gameweek = firstGameweek + offset;
    columns.set(gameweek, {
      gameweek,
      points: null,
      minutes: null,
      fixtures: [],
      isProvisional: false
    });
  }

  if (detailedPlayer) {
    for (const fixture of detailedPlayer.fixtures) {
      const column = columns.get(fixture.gameweek);
      if (!column) continue;
      column.points = (column.points ?? 0) + fixture.predictions.points;
      column.minutes = (column.minutes ?? 0) + fixture.predictions.minutes;
      column.fixtures.push({
        opponentShortName: fixture.opponent.shortName,
        isHome: fixture.isHome
      });
      if (fixture.likelihood < CONFIRMED_FIXTURE_LIKELIHOOD) {
        column.isProvisional = true;
      }
    }
    return [...columns.values()];
  }

  // The caller's payload is already on the client, so it seeds the grid
  // (points and opponents, no expected minutes) while the richer players
  // response loads.
  for (const fixture of seedFixtures) {
    const column = columns.get(fixture.gameweek);
    if (!column) continue;
    if (fixture.predictedPoints != null) {
      column.points = (column.points ?? 0) + fixture.predictedPoints;
    }
    column.fixtures.push({
      opponentShortName: fixture.opponent.shortName,
      isHome: fixture.isHome
    });
  }
  return [...columns.values()];
};

const RoleLetterChip = ({ letter }: { letter: string }) => (
  <span
    aria-hidden
    className="flex items-center justify-center rounded-full border-2 border-current w-5 h-5 md:w-6 md:h-6 text-[10px] md:text-xs font-bold leading-none shrink-0"
  >
    {letter}
  </span>
);

const ButtonContent = ({ children }: { children: React.ReactNode }) => (
  <span className="flex items-center justify-center gap-2 whitespace-nowrap">
    {children}
  </span>
);

const SectionHeader = ({
  title,
  aside
}: {
  title: React.ReactNode;
  aside: React.ReactNode;
}) => (
  <div className="flex items-baseline justify-between">
    <span className="font-sans text-sm font-semibold text-neutral-800">
      {title}
    </span>
    <span className="text-[11px] md:text-xs text-neutral-600">{aside}</span>
  </div>
);

type IdentityHeaderProps = {
  player: PlayerInfoSubject;
  detailedPlayer: Player | null;
  isMobileViewport: boolean;
};

/**
 * Portrait, club badge, position chip, name, sub-line and the price /
 * availability / ownership pills. The silhouette shows immediately and the
 * real portrait fades in when the players response lands, matching the
 * team-entry pitch (no loading gate).
 */
const IdentityHeader = ({
  player,
  detailedPlayer,
  isMobileViewport
}: IdentityHeaderProps) => {
  const [isPortraitLoaded, setIsPortraitLoaded] = useState(false);
  const [hasPortraitFailed, setHasPortraitFailed] = useState(false);

  const portraitSize = isMobileViewport
    ? MOBILE_PORTRAIT_SIZE
    : DESKTOP_PORTRAIT_SIZE;
  const imageUrl = detailedPlayer?.imageUrl ?? null;
  const shouldShowPortrait = Boolean(imageUrl) && !hasPortraitFailed;

  const teamName = detailedPlayer?.team.fullName ?? player.teamShortName;
  const ownership = detailedPlayer?.ownership ?? null;
  // A fit player needs no pill: the absence of a flag is the good news.
  const availabilityLabel = getAvailabilityLabel(
    player.status,
    player.chanceOfPlaying
  );
  const availabilitySeverity = getAvailabilitySeverity(
    player.status,
    player.chanceOfPlaying
  );
  const shouldWarnAvailability = availabilitySeverity !== 'none';

  const pricePill = (
    <span className="inline-flex items-center h-[22px] md:h-[26px] px-2 md:px-2.5 rounded-full bg-primary-600 text-white text-xs md:text-sm font-bold whitespace-nowrap">
      £{player.price.toFixed(1)}m
    </span>
  );
  const availabilityPill = availabilityLabel ? (
    <span
      className={`inline-flex items-center gap-1 h-[22px] md:h-6 px-2 md:px-2.5 rounded-full text-xs font-semibold whitespace-nowrap ${AVAILABILITY_PILL_STYLES[availabilitySeverity]}`}
      data-cy="player-info-availability"
      data-cy-availability={availabilitySeverity}
    >
      {shouldWarnAvailability && (
        <Icon name="Warning" aria-hidden className="w-3 h-3 shrink-0" />
      )}
      {availabilityLabel}
    </span>
  ) : null;

  return (
    <div className="bg-neutral-100 border-b border-neutral-200 px-4 pt-4 pb-3.5 md:px-[22px] md:py-5">
      <div className="flex items-center gap-3.5 md:gap-4 pr-7">
        <div className="relative shrink-0">
          <PlayerCircle
            size={portraitSize}
            hideSilhouette={shouldShowPortrait && isPortraitLoaded}
          >
            {shouldShowPortrait && imageUrl && (
              <Image
                src={imageUrl}
                alt={player.displayName}
                fill
                sizes={`${portraitSize}px`}
                className={`object-cover transition duration-300 ease-out ${
                  isPortraitLoaded
                    ? 'scale-100 opacity-100'
                    : 'scale-150 opacity-0'
                }`}
                onLoad={() => setIsPortraitLoaded(true)}
                onError={() => setHasPortraitFailed(true)}
                data-cy="player-action-portrait"
              />
            )}
          </PlayerCircle>
          <div className="absolute -bottom-1.5 -left-2 z-10">
            <ClubBadgeCircle teamCode={player.teamShortName} />
          </div>
          <span
            className="absolute -bottom-1.5 -right-2 z-10 flex items-center justify-center rounded-full bg-neutral-800 text-white text-[10px] font-bold leading-none px-1.5 border border-white"
            style={{ height: CONTROL_SIZE, minWidth: CONTROL_SIZE }}
            data-cy="player-action-position"
          >
            {player.position}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-sans text-[22px] font-bold leading-[1.15] md:text-2xl md:font-normal md:leading-tight text-neutral-800 truncate">
            {player.displayName}
          </h3>
          <div className="flex items-center gap-1.5 mt-1 text-[13px] md:text-sm text-neutral-600">
            <span className="truncate">{teamName}</span>
            <span
              aria-hidden
              className="w-1 h-1 rounded-full bg-neutral-400 shrink-0"
            />
            <span className="whitespace-nowrap">
              {POSITION_FULL_NAMES[player.position]}
            </span>
            {ownership != null && (
              <>
                <span
                  aria-hidden
                  className="hidden md:block w-1 h-1 rounded-full bg-neutral-400 shrink-0"
                />
                <span className="hidden md:inline whitespace-nowrap">
                  {ownership.toFixed(1)}% owned
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-2 md:hidden">
            {pricePill}
            {availabilityPill}
            {ownership != null && (
              <span className="inline-flex items-center h-[22px] px-2 rounded-full bg-neutral-200 text-neutral-700 text-xs font-semibold whitespace-nowrap">
                {ownership.toFixed(1)}% owned
              </span>
            )}
          </div>
        </div>
        <div className="hidden md:flex flex-col items-end gap-1.5 shrink-0">
          {pricePill}
          {availabilityPill}
        </div>
      </div>
      <PlayerNewsNote
        news={detailedPlayer?.news}
        newsAddedAt={detailedPlayer?.newsAddedAt}
        className="mt-3"
      />
    </div>
  );
};

const PredictionTile = ({ column }: { column: PredictionColumn }) => {
  const hasFixture = column.fixtures.length > 0;

  return (
    <div className="flex flex-col items-center gap-1 md:gap-[5px]">
      <span className="text-[10px] md:text-[11px] font-semibold text-neutral-600">
        GW{column.gameweek}
      </span>
      {hasFixture ? (
        <div
          className={`w-full rounded-lg text-center px-0.5 pt-1.5 pb-[5px] md:px-1 md:pt-2 md:pb-[7px] ${
            column.points != null
              ? getPredictedPointsLevelStyles(column.points)
              : 'bg-neutral-100 text-neutral-800'
          } ${column.isProvisional ? 'opacity-60' : ''}`}
        >
          <div className="text-[15px] md:text-lg font-bold leading-[1.1]">
            {column.points != null ? column.points.toFixed(1) : '–'}
          </div>
          {column.fixtures.map((fixture, index) => (
            <div
              key={`${fixture.opponentShortName}-${index}`}
              className="text-[9px] md:text-[10px] font-bold uppercase mt-0.5"
            >
              {fixture.opponentShortName} ({fixture.isHome ? 'H' : 'A'})
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full rounded-lg bg-neutral-100 text-center px-0.5 pt-1.5 pb-[5px] md:px-1 md:pt-2 md:pb-[7px]">
          <div className="text-[15px] md:text-lg font-bold leading-[1.1] text-neutral-400">
            –
          </div>
          <div className="text-[9px] md:text-[10px] font-bold uppercase mt-0.5 text-neutral-400">
            Blank
          </div>
        </div>
      )}
      <span className="text-[9px] md:text-[10px] text-neutral-600">
        {column.minutes != null && hasFixture
          ? `${Math.round(column.minutes)}'`
          : '\u00A0'}
      </span>
    </div>
  );
};

const LockedPredictionTile = ({ gameweek }: { gameweek: number }) => (
  <div className="flex flex-col items-center gap-1 md:gap-[5px]">
    <span className="text-[10px] md:text-[11px] font-semibold text-neutral-600">
      GW{gameweek}
    </span>
    <div className="w-full rounded-lg bg-neutral-100 flex items-center justify-center px-0.5 py-2.5 md:py-3.5">
      <Icon name="Lock" aria-hidden className="w-4 h-4 text-neutral-400" />
    </div>
    <span aria-hidden className="text-[9px] md:text-[10px]">
      {' '}
    </span>
  </div>
);

const MembershipCta = () => (
  <div className="mt-2.5 p-4 border border-dashed border-primary-200 rounded-lg bg-primary-50 text-center">
    <p className="font-sans text-sm font-semibold text-neutral-800">
      Points predictions are a members-only feature
    </p>
    <p className="mt-1.5 mb-3 text-xs leading-[18px] text-neutral-700">
      See predicted points, expected minutes and fixture difficulty for the next
      six gameweeks.
    </p>
    <Button variant="primary" href="/join" data-cy="player-info-join">
      Become a member
    </Button>
  </div>
);

type PredictionsSectionProps = {
  columns: PredictionColumn[];
  isPayingMember: boolean;
  hasDegradedPredictions: boolean;
};

const PredictionsSection = ({
  columns,
  isPayingMember,
  hasDegradedPredictions
}: PredictionsSectionProps) => {
  const totalPoints = columns.reduce(
    (total, column) => total + (column.points ?? 0),
    0
  );
  const hasFreeColumn = columns.some(
    (column) => column.gameweek === FREE_PREDICTIONS_GAMEWEEK
  );

  return (
    <div className="px-4 pt-3.5 md:px-[22px] md:pt-[18px]">
      <SectionHeader
        title={
          <>
            Predicted points
            <span className="hidden md:inline">, next 6 gameweeks</span>
          </>
        }
        aside={
          isPayingMember ? (
            <>
              <span className="md:hidden">Next 6 GWs</span>
              <span className="hidden md:inline">
                {totalPoints.toFixed(1)} pts total
              </span>
            </>
          ) : (
            'Next 6 GWs'
          )
        }
      />
      {isPayingMember || hasFreeColumn ? (
        <>
          <div
            className="grid grid-cols-6 gap-1.5 md:gap-2 mt-2.5 md:mt-3"
            data-cy="player-info-predictions"
          >
            {columns.map((column) =>
              isPayingMember ||
              column.gameweek === FREE_PREDICTIONS_GAMEWEEK ? (
                <PredictionTile key={column.gameweek} column={column} />
              ) : (
                <LockedPredictionTile
                  key={column.gameweek}
                  gameweek={column.gameweek}
                />
              )
            )}
          </div>
          {hasDegradedPredictions && isPayingMember && (
            <p className="mt-1 text-[11px] text-warning-700">
              Predictions are temporarily served from earlier data and may be
              slightly stale.
            </p>
          )}
          {isPayingMember ? (
            <div className="flex items-center justify-between mt-3 px-3 py-2 rounded-lg bg-primary-100 md:hidden">
              <span className="text-xs font-semibold text-primary-700">
                Total over 6 GWs
              </span>
              <span className="font-sans text-base font-bold text-primary-700">
                {totalPoints.toFixed(1)} pts
              </span>
            </div>
          ) : (
            <MembershipCta />
          )}
        </>
      ) : (
        <MembershipCta />
      )}
    </div>
  );
};

type StatCardProps = {
  label: string;
  realised: number;
  expected: number;
  expectedLabel: string;
  /** Colours the chip green when realised is below expected instead. */
  isUnderExpectedGood?: boolean;
};

const StatCard = ({
  label,
  realised,
  expected,
  expectedLabel,
  isUnderExpectedGood = false
}: StatCardProps) => {
  const delta = realised - expected;
  const isOverExpected = delta > DELTA_EPSILON;
  const isUnderExpected = delta < -DELTA_EPSILON;
  const isGoodDelta = isUnderExpectedGood ? isUnderExpected : isOverExpected;

  return (
    <div className="p-2 md:p-3 border border-neutral-200 rounded-lg min-h-[86px] md:min-h-[104px]">
      <p className="text-[9px] md:text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-600">
        {label}
      </p>
      <p className="font-sans text-xl md:text-[26px] font-bold leading-[1.1] text-neutral-800 mt-[3px] md:mt-[5px]">
        {realised.toFixed(2)}
        <span className="text-[10px] md:text-xs font-medium text-neutral-600">
          {' '}
          / start
        </span>
      </p>
      <p className="text-[10px] md:text-xs text-neutral-600 mt-0.5">
        <span className="hidden md:inline">vs </span>
        {expected.toFixed(2)}{' '}
        <span className="md:font-semibold">{expectedLabel}</span>
      </p>
      {(isOverExpected || isUnderExpected) && (
        <span
          className={`inline-flex items-center gap-0.5 mt-[5px] px-[5px] py-px rounded-[5px] text-[9px] md:text-[10px] font-bold ${
            isGoodDelta
              ? 'bg-success-100 text-success-800'
              : 'bg-danger-50 text-danger-600'
          }`}
        >
          <span aria-hidden>{isOverExpected ? '↑' : '↓'}</span>
          {isOverExpected ? '+' : ''}
          {delta.toFixed(2)}
          <span className="hidden md:inline font-medium">vs expected</span>
        </span>
      )}
    </div>
  );
};

const SecondaryStatTile = ({
  value,
  label,
  isPerStart = false
}: {
  value: string;
  label: string;
  /** Appends the per-start unit; off for raw counts like starts. */
  isPerStart?: boolean;
}) => (
  <div className="flex flex-col items-center gap-0.5 px-1 py-[7px] rounded-lg bg-neutral-100">
    <span className="font-sans text-sm font-bold text-neutral-800">
      {value}
      {isPerStart && (
        <span className="text-[10px] font-medium text-neutral-600">
          {' '}
          / start
        </span>
      )}
    </span>
    <span className="text-[9px] font-semibold text-center text-neutral-600">
      {label}
    </span>
  </div>
);

/**
 * Same shells as the cards above, with the figures replaced by a padlock: the
 * labels still say what a member would get. Heights match their unlocked
 * counterparts so the section does not resize on upgrade.
 */
const LockedStatCard = ({ label }: { label: string }) => (
  <div className="p-2 md:p-3 border border-neutral-200 rounded-lg min-h-[86px] md:min-h-[104px]">
    <p className="text-[9px] md:text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-600">
      {label}
    </p>
    <div className="flex items-center justify-center h-[52px] md:h-[66px]">
      <Icon
        name="Lock"
        aria-label={`${label} is a members-only stat`}
        className="w-4 h-4 md:w-5 md:h-5 text-neutral-400"
      />
    </div>
  </div>
);

const LockedSecondaryTile = ({ label }: { label: string }) => (
  <div className="flex flex-col items-center gap-0.5 px-1 py-[7px] rounded-lg bg-neutral-100">
    <Icon
      name="Lock"
      aria-label={`${label} is a members-only stat`}
      className="w-3.5 h-3.5 text-neutral-400"
    />
    <span className="text-[9px] font-semibold text-center text-neutral-600">
      {label}
    </span>
  </div>
);

type SeasonToggleProps = {
  seasons: number[];
  activeSeason: number | null;
  onSelectSeason: (season: number) => void;
};

const SeasonToggle = ({
  seasons,
  activeSeason,
  onSelectSeason
}: SeasonToggleProps) => (
  <span
    className="inline-flex items-center gap-0.5 p-0.5 rounded-full bg-neutral-100"
    data-cy="player-info-season-toggle"
  >
    {seasons.map((season) => {
      const isActive = season === activeSeason;
      return (
        <button
          key={season}
          type="button"
          onClick={() => onSelectSeason(season)}
          aria-pressed={isActive}
          className={`px-2 py-px rounded-full text-[10px] md:text-[11px] font-semibold whitespace-nowrap transition-colors ${
            isActive
              ? 'bg-white text-neutral-800 shadow-100'
              : 'text-neutral-600 hover:text-neutral-800'
          }`}
        >
          {formatSeasonLabel(season)}
        </button>
      );
    })}
  </span>
);

type SeasonStatsSectionProps = {
  layout: SeasonStatsLayout;
  statsRow: PlayerStatsRow | null;
  /** Season the row covers, which need not be the current one. */
  statsSeason: number | null;
  isLoading: boolean;
  seasons: number[];
  onSelectSeason: (season: number) => void;
  isPayingMember: boolean;
};

const SeasonStatsSection = ({
  layout,
  statsRow,
  statsSeason,
  isLoading,
  seasons,
  onSelectSeason,
  isPayingMember
}: SeasonStatsSectionProps) => {
  // Under perStart aggregation the API scopes participation to started
  // matches, so `appearances` is filtered down to equal `starts` and is not a
  // usable denominator. The starts count is the sample size these per-start
  // figures rest on, so show that alone.
  const starts = statsRow
    ? readStatNumber(statsRow, 'participation.starts')
    : 0;

  return (
    <div className="px-4 pt-4 md:px-[22px] md:pt-[18px]">
      <SectionHeader
        title="Season stats"
        aside={
          isPayingMember ? (
            <SeasonToggle
              seasons={seasons}
              activeSeason={statsSeason}
              onSelectSeason={onSelectSeason}
            />
          ) : (
            // Nothing to toggle between while the values are locked.
            <span className="inline-flex items-center gap-1 px-2 py-px rounded-full bg-neutral-100 text-[10px] md:text-[11px] font-semibold text-neutral-600 whitespace-nowrap">
              <Icon name="Lock" aria-hidden className="w-2.5 h-2.5" />
              Members only
            </span>
          )
        }
      />
      {/* Free members see labels with no values, so the line has to say the
          figures are per start; for members the values carry the unit
          themselves and the line is just the sample size. The non-breaking
          space holds the row height while the stats load. */}
      <p className="mt-1 text-[10px] md:text-[11px] text-neutral-600">
        {isPayingMember
          ? starts > 0
            ? `Across ${starts} start${starts === 1 ? '' : 's'}`
            : '\u00A0'
          : 'Per start'}
      </p>
      {!isPayingMember ? (
        // The block stays visible so the reader can see what membership adds,
        // with the figures themselves padlocked as they are on the Opta page.
        <>
          <div
            className="grid grid-cols-3 gap-1.5 md:gap-2 mt-2.5 md:mt-3"
            data-cy="player-info-opta-locked"
          >
            {layout.cards.map((card) => (
              <LockedStatCard key={card.label} label={card.label} />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-1.5 md:gap-2 mt-1.5">
            {layout.tiles.map((tile) => (
              <LockedSecondaryTile key={tile.label} label={tile.label} />
            ))}
            <LockedSecondaryTile label="Starts" />
          </div>
        </>
      ) : statsRow ? (
        <>
          <div
            className="grid grid-cols-3 gap-1.5 md:gap-2 mt-2.5 md:mt-3"
            data-cy="player-info-opta-stats"
          >
            {/* Realised and expected both come off the same row, so a card
                always describes one season and one aggregation. */}
            {layout.cards.map((card) => (
              <StatCard
                key={card.label}
                label={card.label}
                realised={readStatNumber(statsRow, card.realisedPath)}
                expected={readStatNumber(statsRow, card.expectedPath)}
                expectedLabel={card.expectedLabel}
                isUnderExpectedGood={card.isUnderExpectedGood}
              />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-1.5 md:gap-2 mt-1.5">
            {/* These are perStart figures like everything else in the section
                (the API's per90 is a separate aggregation), so each value
                carries the "/ start" unit rather than claiming /90. */}
            {layout.tiles.map((tile) => (
              <SecondaryStatTile
                key={tile.label}
                value={readStatNumber(statsRow, tile.path).toFixed(1)}
                label={tile.label}
                isPerStart
              />
            ))}
            <SecondaryStatTile
              value={starts > 0 ? starts.toString() : '–'}
              label="Starts"
            />
          </div>
        </>
      ) : isLoading ? (
        // The stats request is a second call; skeleton cards keep the modal
        // interactive instead of blocking on it.
        <div
          className="grid grid-cols-3 gap-1.5 md:gap-2 mt-2.5 md:mt-3"
          data-cy="player-info-opta-loading"
        >
          <div className="h-[86px] md:h-[104px] rounded-lg bg-neutral-100" />
          <div className="h-[86px] md:h-[104px] rounded-lg bg-neutral-100" />
          <div className="h-[86px] md:h-[104px] rounded-lg bg-neutral-100" />
        </div>
      ) : (
        <p
          className="mt-2.5 md:mt-3 p-3 rounded-lg bg-neutral-100 text-xs text-neutral-600 text-center"
          data-cy="player-info-opta-empty"
        >
          {statsSeason != null
            ? `No Premier League appearances in ${formatSeasonLabel(
                statsSeason
              )}.`
            : 'No Premier League stats for this player yet.'}
        </p>
      )}
    </div>
  );
};

export const PlayerInfoModal = ({
  subject,
  onClose,
  startGameweek,
  actions = null,
  isPayingMember = true
}: PlayerInfoModalProps) => {
  const isMobileViewport = useIsMobileViewport();
  const { accessToken } = useAccessToken();
  const { context } = useLeagueContext();
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);

  const playerId = subject?.id ?? null;
  const isOpen = playerId != null;
  // The subject is only null while the modal is closed, when both stats
  // queries are disabled, so the placeholder layout is never requested.
  const seasonStatsLayout = subject
    ? SEASON_STATS_LAYOUTS[subject.position]
    : FORWARD_STATS_LAYOUT;

  // Each player gets the season that actually holds their data, so a manual
  // choice must not follow the reader on to the next player.
  useEffect(() => {
    setSelectedSeason(null);
  }, [playerId]);

  const { data: rawPlayersData, isPreviousData: isPlayersDataPrevious } =
    usePlayers({
      ids: playerId ?? undefined,
      minGameweek: startGameweek,
      maxGameweek: startGameweek + PREDICTED_GAMEWEEK_COUNT - 1,
      limit: 1,
      enabled: isOpen,
      staleTime: PLAYER_DETAIL_STALE_TIME_MS,
      accessToken: accessToken ?? undefined
    });
  // keepPreviousData would otherwise hand this player the previous player's
  // row: their portrait, ownership and prediction grid, swapped out mid-read
  // when the real response lands. The subject's seed data covers the gap.
  const playersData = isPlayersDataPrevious ? undefined : rawPlayersData;
  const detailedPlayer = playersData?.pages[0]?.data[0] ?? null;
  const hasDegradedPredictions =
    playersData?.pages[0]?.meta.isDegraded ?? false;

  const currentSeason = context?.season ?? null;
  const isPreSeason = context?.seasonStatus === 'PRE_SEASON';
  const lastSeason =
    currentSeason != null ? currentSeason - PREVIOUS_SEASON_OFFSET : null;
  // `externalIds` is absent from some responses, so it is read defensively and
  // falls back to the deprecated `optaId`, which carries the same value. An
  // unguarded read here threw once the response landed and took the whole tree
  // down with it, which is how it presented: elements vanishing mid-press.
  const optaUuid =
    detailedPlayer?.externalIds?.optaUuid ?? detailedPlayer?.optaId ?? null;

  // Free members get the block padlocked, so there is nothing to fetch for
  // them at all.
  const canReadStats = isOpen && isPayingMember;

  // Before a ball is kicked the new season has no Opta output for anyone, so
  // it is not requested until the reader explicitly asks for it.
  const isThisSeasonRequested =
    !isPreSeason || selectedSeason === currentSeason;
  const thisSeasonStats = usePlayerSeasonStats({
    playerId,
    optaUuid,
    fields: seasonStatsLayout.fields,
    season: currentSeason,
    currentSeason,
    enabled: canReadStats && isThisSeasonRequested,
    accessToken
  });

  // Last season is worth a request when the current one has nothing to show:
  // pre-season, a player yet to start, or a reader who toggled back to it.
  const isLastSeasonRequested =
    isPreSeason || selectedSeason === lastSeason || thisSeasonStats.isEmpty;
  const lastSeasonStats = usePlayerSeasonStats({
    playerId,
    optaUuid,
    fields: seasonStatsLayout.fields,
    season: lastSeason,
    currentSeason,
    enabled: canReadStats && isLastSeasonRequested,
    accessToken
  });

  // Open on the season expected to hold data, and move off it only once that
  // is known to be empty while the other has something. Anything looser makes
  // the toggle jump as the two requests settle.
  const expectedSeason = isPreSeason ? lastSeason : currentSeason;
  const isExpectedThisSeason = expectedSeason === currentSeason;
  const expectedStats = isExpectedThisSeason
    ? thisSeasonStats
    : lastSeasonStats;
  const otherStats = isExpectedThisSeason ? lastSeasonStats : thisSeasonStats;
  const defaultSeason =
    expectedStats.isEmpty && otherStats.row
      ? isExpectedThisSeason
        ? lastSeason
        : currentSeason
      : expectedSeason;

  const activeSeason = selectedSeason ?? defaultSeason;
  const activeStats =
    activeSeason != null && activeSeason === currentSeason
      ? thisSeasonStats
      : lastSeasonStats;
  const seasonOptions =
    currentSeason != null && lastSeason != null
      ? [lastSeason, currentSeason]
      : [];
  // A season we don't know yet (league context still loading) counts as
  // loading, or the empty state would flash before the first request fires.
  const isStatsLoading =
    activeStats.row == null && (currentSeason == null || activeStats.isLoading);

  // Open on the press, without waiting for any request: a tap that appears to
  // do nothing reads as broken, and the pitch re-taps that follow land on the
  // overlay and shut the dialog again.
  if (!subject) return null;

  const predictionColumns = buildPredictionColumns(
    startGameweek,
    detailedPlayer,
    subject.seedFixtures
  );

  return (
    <FFHModal
      open
      handleClose={onClose}
      title="Player info"
      bottom={isMobileViewport}
      iconColor="text-neutral-400"
      childrenClassName={
        isMobileViewport ? 'rounded-t-lg' : 'w-[540px] max-w-[95vw]'
      }
    >
      <div data-cy="player-action-menu">
        <IdentityHeader
          key={subject.id}
          player={subject}
          detailedPlayer={detailedPlayer}
          isMobileViewport={isMobileViewport}
        />

        <PredictionsSection
          columns={predictionColumns}
          isPayingMember={isPayingMember}
          hasDegradedPredictions={hasDegradedPredictions}
        />

        <SeasonStatsSection
          layout={seasonStatsLayout}
          statsRow={activeStats.row}
          statsSeason={activeSeason}
          isLoading={isStatsLoading}
          seasons={seasonOptions}
          onSelectSeason={setSelectedSeason}
          isPayingMember={isPayingMember}
        />

        {actions ? (
          // The sheet's bottom edge is the browser's own territory on a
          // phone (toolbar, home-indicator gesture zone) and taps landing
          // there never reach the page, so the bottom button row keeps
          // safe-area clearance on top of its own padding.
          <div className="p-4 pb-[calc(2rem+env(safe-area-inset-bottom))] md:px-[22px] md:py-5">
            {actions.isTransferModeActive && (
              <BodyRegular textColor="text-warning-700" className="mb-3">
                Finish or cancel your pending transfers before changing captain,
                vice-captain or substitutes.
              </BodyRegular>
            )}
            {/* 2x2 per the design. Labels are held on one line and kept short
                enough to fit a column at phone width; the disabled state is
                what says a player already holds the armband. */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="primary"
                disabled={
                  actions.isCaptain ||
                  actions.isPending ||
                  actions.isTransferModeActive
                }
                onClick={actions.onMakeCaptain}
                data-cy="player-action-make-captain"
              >
                <ButtonContent>
                  <RoleLetterChip letter="C" />
                  Make captain
                </ButtonContent>
              </Button>
              <Button
                variant="secondary"
                disabled={
                  actions.isViceCaptain ||
                  actions.isPending ||
                  actions.isTransferModeActive
                }
                onClick={actions.onMakeViceCaptain}
                data-cy="player-action-make-vice-captain"
              >
                <ButtonContent>
                  <RoleLetterChip letter="V" />
                  Vice-captain
                </ButtonContent>
              </Button>
              <Button
                variant="secondary"
                disabled={actions.isPending || actions.isTransferModeActive}
                onClick={actions.onStartSubstitution}
                data-cy="player-action-substitute"
              >
                <ButtonContent>
                  <Icon
                    name="CompareArrows"
                    aria-hidden
                    className="w-5 h-5 md:w-6 md:h-6 shrink-0"
                  />
                  Substitute
                </ButtonContent>
              </Button>
              <Button
                variant="secondary"
                disabled={actions.isPending}
                onClick={actions.onTransferOut}
                data-cy="player-action-transfer-out"
              >
                <ButtonContent>
                  <Icon
                    name="PlayerOut"
                    aria-hidden
                    className="w-5 h-5 md:w-6 md:h-6 shrink-0"
                  />
                  Transfer out
                </ButtonContent>
              </Button>
            </div>
            {/* Hidden rather than disabled in transfer mode, matching the
                app's action menu: asking for a recommendation mid-transfer
                would seed the builder behind the user's back. */}
            {!actions.isTransferModeActive && actions.canRecommendTransfer && (
              <div className="mt-2">
                {isPayingMember ? (
                  <Button
                    variant="secondary"
                    className="w-full"
                    disabled={actions.isPending}
                    onClick={actions.onRecommendTransfer}
                    data-cy="player-action-recommend-transfer"
                  >
                    <ButtonContent>
                      <Icon
                        name="SparkleFilled"
                        aria-hidden
                        className="w-5 h-5 md:w-6 md:h-6 shrink-0"
                      />
                      Recommend transfer
                    </ButtonContent>
                  </Button>
                ) : (
                  <LockedAction
                    label="Recommend transfer"
                    feature="AI transfer recommendations"
                    placement={RECOMMEND_TRANSFER_PLACEMENT}
                    dataCy="player-action-recommend-transfer-locked"
                    className="w-full"
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="pb-[calc(2rem+env(safe-area-inset-bottom))] md:pb-5" />
        )}
      </div>
    </FFHModal>
  );
};
